import { parseHHMMMinutes } from './hhmm';
import { SECS_PER_MIN } from './leg-format';
import type { HafasDepartureRowJson } from '../types/HafasDepartureRowJson';

/** Rendered delay badge: text plus a class controlling color. */
export interface DelayBadgeView {
  text: string;
  cssClass: 'delay-cancelled' | 'delay-zero' | 'delay-positive' | 'delay-negative';
}

/** CSS class picking the departure-time color. */
type TimeColorClass = 'time-normal' | 'time-muted' | 'time-late' | 'time-early' | 'time-cancelled';

/** Full per-row derivation the departures board renders. */
export interface DepartureRowView {
  /** Time text: scheduled `HH:MM` when cancelled, `~HH:MM` when no realtime, otherwise effective `HH:MM`. */
  label: string;
  /** CSS class for time color. */
  timeClass: TimeColorClass;
  /** Delay badge to reveal on expand; `undefined` when no badge is warranted. */
  delayView: DelayBadgeView | undefined;
  /** Hover / aria-description text for the row. */
  hoverTitle: string;
}

/** Absolute-delay threshold (seconds) below which the time color stays normal, keeping sub-minute noise off the board. */
const SIGNIFICANT_DELAY_SECONDS = 60;

/** Below this absolute delay (seconds), the badge shows seconds; above, whole minutes. */
const SECONDS_PRECISION_THRESHOLD = 60;

function badgeSign(delaySeconds: number): '+' | '-' {
  return delaySeconds > 0 ? '+' : '-';
}

function badgeCssClass(delaySeconds: number): DelayBadgeView['cssClass'] {
  return delaySeconds > 0 ? 'delay-positive' : 'delay-negative';
}

function formatBadgeForNonZero(delaySeconds: number): DelayBadgeView {
  const sign = badgeSign(delaySeconds);
  const cssClass = badgeCssClass(delaySeconds);
  const absDelay = Math.abs(delaySeconds);
  if (absDelay < SECONDS_PRECISION_THRESHOLD) {
    return { text: `${sign}${String(absDelay)}s`, cssClass };
  }
  return { text: `${sign}${String(Math.floor(absDelay / SECS_PER_MIN))} min`, cssClass };
}

/**
 * Formats a raw delay/cancellation state into the {@link DelayBadgeView} the
 * UI renders (or undefined when there is no badge to show). Cancelled overrides
 * everything; missing realtime or absent `delaySeconds` yields undefined;
 * sub-minute delays render as seconds; larger delays FLOOR to whole minutes so
 * the label matches the HH:MM clock the user reads.
 */
export function formatDelay(
  delaySeconds: number | null | undefined,
  isCancelled: boolean,
  isRealtimeAvailable: boolean,
): DelayBadgeView | undefined {
  if (isCancelled) return { text: 'cancelled', cssClass: 'delay-cancelled' };
  if (!isRealtimeAvailable || delaySeconds === null || delaySeconds === undefined) return undefined;
  if (delaySeconds === 0) return { text: '+0', cssClass: 'delay-zero' };
  return formatBadgeForNonZero(delaySeconds);
}

/**
 * Board-row badge derived from the two HH:MM strings the user actually sees.
 * When both `scheduled` and `effective` parse cleanly the badge sign, colour,
 * and magnitude all track the visible clock shift `effectiveMin −
 * scheduledMin`, so the badge always matches what the rider reads on the board.
 * When the clock stays inside one minute the sub-minute delay renders as
 * seconds. Falls back to {@link formatBadgeForNonZero} (floor) when either
 * string is malformed.
 */
function formatBadgeFromClock(
  scheduled: string,
  effective: string,
  delaySeconds: number,
): DelayBadgeView {
  const scheduledMin = parseHHMMMinutes(scheduled);
  const effectiveMin = parseHHMMMinutes(effective);
  if (scheduledMin === undefined || effectiveMin === undefined) {
    return formatBadgeForNonZero(delaySeconds);
  }
  const visibleShift = effectiveMin - scheduledMin;
  if (visibleShift === 0) {
    return sameMinuteBadge(delaySeconds);
  }
  const sign = visibleShift > 0 ? '+' : '-';
  const cssClass: DelayBadgeView['cssClass'] =
    visibleShift > 0 ? 'delay-positive' : 'delay-negative';
  return { text: `${sign}${String(Math.abs(visibleShift))} min`, cssClass };
}

/** Badge for the rare case where the visible clock stayed inside one minute:
 * sub-minute delays render as seconds, larger ones floor to whole minutes so
 * a same-minute row still communicates a >= 60s drift. */
function sameMinuteBadge(delaySeconds: number): DelayBadgeView {
  const sign = badgeSign(delaySeconds);
  const cssClass = badgeCssClass(delaySeconds);
  const absDelay = Math.abs(delaySeconds);
  if (absDelay < SECONDS_PRECISION_THRESHOLD) {
    return { text: `${sign}${String(absDelay)}s`, cssClass };
  }
  return {
    text: `${sign}${String(Math.floor(absDelay / SECS_PER_MIN))} min`,
    cssClass,
  };
}

/** Board-row equivalent of {@link formatDelay} that pins the badge to the visible clock shift. */
function badgeForRow(entry: HafasDepartureRowJson): DelayBadgeView | undefined {
  if (entry.cancelled) return { text: 'cancelled', cssClass: 'delay-cancelled' };
  const delay = entry.delay_seconds === null ? undefined : Number(entry.delay_seconds);
  if (delay === undefined || !entry.realtime) return undefined;
  if (delay === 0) return { text: '+0', cssClass: 'delay-zero' };
  return formatBadgeFromClock(entry.scheduled, entry.effective, delay);
}

/** Color once we know the row is live and delay is a definite number. */
function colorForLiveDelay(delaySecs: number): TimeColorClass {
  if (Math.abs(delaySecs) < SIGNIFICANT_DELAY_SECONDS) return 'time-normal';
  return delaySecs > 0 ? 'time-late' : 'time-early';
}

function pickTimeClass(
  entry: HafasDepartureRowJson,
  delaySecs: number | undefined,
): TimeColorClass {
  if (entry.cancelled) return 'time-cancelled';
  if (!entry.realtime) return 'time-muted';
  if (typeof delaySecs !== 'number') return 'time-normal';
  return colorForLiveDelay(delaySecs);
}

function pickLabel(entry: HafasDepartureRowJson): string {
  if (entry.cancelled) return entry.scheduled;
  return entry.realtime ? entry.effective : `~${entry.scheduled}`;
}

function pickHoverTitle(entry: HafasDepartureRowJson): string {
  if (entry.cancelled) return `Cancelled — was scheduled ${entry.scheduled}`;
  if (entry.realtime) {
    return `Predicted ${entry.effective} (scheduled ${entry.scheduled}, real-time from HAFAS)`;
  }
  return `Scheduled ${entry.scheduled} (no real-time data — treat as approximate)`;
}

/**
 * Assembles the per-row view the {@link DepartureRow} component reads:
 * label, time-color class, optional delay badge, and hover title. The badge
 * minutes are derived from `|effectiveMin − scheduledMin|` so they always
 * match the HH:MM shift the rider sees on the board.
 */
export function deriveRowView(entry: HafasDepartureRowJson): DepartureRowView {
  const delaySecs = entry.delay_seconds === null ? undefined : Number(entry.delay_seconds);
  return {
    label: pickLabel(entry),
    timeClass: pickTimeClass(entry, delaySecs),
    delayView: badgeForRow(entry),
    hoverTitle: pickHoverTitle(entry),
  };
}
